/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/*
 * Zero impactScoresByIVV
 *
 * NOTE: includes impactScoreDatums even for N/A impact-valence pairs
 */
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import {
  chain,
  cloneDeep,
  Dictionary,
  forIn,
  get,
  groupBy,
  keyBy,
  keys,
  mapValues,
  set,
  sum,
  sumBy,
  values,
} from 'lodash';
import { NumberHash } from '../../../utils/number-hash/types';
import { isImpactCategoryVisible } from '../../index';
import { uiImpactCategoryByCode } from '../../input/impacts';
import {
  allDimensions,
  Dimension,
  orderedImpactsByDimension,
  ORDERED_IMPACTS_BY_DIMENSION,
  Valence,
  ValueChain,
} from '../../types';
import { ByIV } from '../score-contributions/types';
import {
  ByImpactValence,
  ImpactScoreAttributesByIV,
  ImpactScoreDatum,
  ImpactScoreIVKey,
  ImpactScoresByDV,
  ImpactScoresByIVV,
  ImpactScoresByPIVV,
  PackedImpactScoresByIVV,
} from './types';

export function getEmptyImpactByIVV(): ImpactScoresByIVV {
  const empty: ImpactScoresByIVV = {};
  const valences = [Valence.POSITIVE, Valence.NEGATIVE];
  for (const impactCode of values(ImpactCategoryId)) {
    empty[impactCode] = {};
    for (const valueChain of values(ValueChain)) {
      empty[impactCode][valueChain] = {};
      for (const valence of valences) {
        empty[impactCode][valueChain][valence] = {
          impactCode,
          valence,
          valueChain,
          value: 0,
        };
      }
    }
  }
  return empty;
}

function getEmptyImpactByDV(): ImpactScoresByDV {
  return {
    [Dimension.ENVIRONMENT]: {
      [Valence.POSITIVE]: 0,
      [Valence.NEGATIVE]: 0,
    },
    [Dimension.HEALTH]: {
      [Valence.POSITIVE]: 0,
      [Valence.NEGATIVE]: 0,
    },
    [Dimension.SOCIAL]: {
      [Valence.POSITIVE]: 0,
      [Valence.NEGATIVE]: 0,
    },
    [Dimension.KNOWLEDGE]: {
      [Valence.POSITIVE]: 0,
      [Valence.NEGATIVE]: 0,
    },
  };
}

export function toImpactScoresByDV(impactScoresByIVV: ImpactScoresByIVV): ImpactScoresByDV {
  const datum = getEmptyImpactByDV();
  Object.keys(impactScoresByIVV).forEach(impactCode => {
    const impactCategory = uiImpactCategoryByCode[impactCode];
    if (impactCategory) {
      const { dimension } = impactCategory;
      values(impactScoresByIVV[impactCode]).forEach(valueChainImpacts => {
        if (valueChainImpacts[Valence.POSITIVE]) {
          datum[dimension][Valence.POSITIVE] += valueChainImpacts[Valence.POSITIVE].value;
        }
        if (valueChainImpacts[Valence.NEGATIVE]) {
          datum[dimension][Valence.NEGATIVE] += valueChainImpacts[Valence.NEGATIVE].value;
        }
      });
    } else {
      console.error('Unknown impact code', impactCode);
    }
  });
  return datum;
}

/**
 * Helper for iterating over ImpactScores
 * in ImpactScoresByIVV objects
 */
export function forEachImpactScoreByIVV(
  impactScoresByIVV: ImpactScoresByIVV,
  iteratee: (impactScore: ImpactScoreDatum) => void,
) {
  // Use for...in loops to avoid creating intermediate arrays with Object.values()
  // This is faster than forEach and avoids function call overhead
  for (const impactCode in impactScoresByIVV) {
    const impactScoresByVV = impactScoresByIVV[impactCode];
    for (const valueChain in impactScoresByVV) {
      const impactScoresByV = impactScoresByVV[valueChain];
      for (const valence in impactScoresByV) {
        iteratee(impactScoresByV[valence]);
      }
    }
  }
}

/**
 * Helper for iterating over ImpactScores
 * in ImpactScoresByIVV objects
 */
export function mapImpactScoreByIVV(
  impactScoresByIVV: ImpactScoresByIVV,
  iteratee: (impactScore: ImpactScoreDatum) => ImpactScoreDatum,
): ImpactScoresByIVV {
  return mapValues(impactScoresByIVV, impactScoresByVV =>
    mapValues(impactScoresByVV, impactScoresByV => mapValues(impactScoresByV, iteratee)),
  );
}

export function filterImpactScoreByIVV(
  impactScoresByIVV: ImpactScoresByIVV,
  predicate: (impactScore: ImpactScoreDatum) => boolean,
): ImpactScoresByIVV {
  const filtered: ImpactScoresByIVV = {};
  forIn(impactScoresByIVV, scoresByVV => {
    forIn(scoresByVV, scoresByV => {
      forIn(scoresByV, score => {
        if (predicate(score)) {
          set(filtered, [score.impactCode, score.valueChain, score.valence], score);
        }
      });
    });
  });
  return filtered;
}

export function flattenedImpactScoreDatums(impactScoresByIVV: ImpactScoresByIVV) {
  const ret: ImpactScoreDatum[] = [];
  forEachImpactScoreByIVV(impactScoresByIVV, datum => ret.push(datum));
  return ret;
}

/**
 * Merge a list of ImpactScoresByIVV,
 * (weighting and) summing them together
 */
export function mergeImpactScores(mergeList: ImpactScoresByIVV[], weightList?: number[]): ImpactScoresByIVV {
  const ret: ImpactScoresByIVV = {};
  const weights = weightList || mergeList.map(_ => 1.0);
  if (weights.length !== mergeList.length) {
    throw Error('mergeList and weightList must be equal length');
  }

  // Use the optimized forEachImpactScoreByIVV instead of nested values().forEach()
  mergeList.forEach((impactScoresByIVV, i) => {
    const weight = weights[i];
    forEachImpactScoreByIVV(impactScoresByIVV, impactScore => {
      const { impactCode, valueChain, valence, value } = impactScore;

      ret[impactCode] ??= {};
      ret[impactCode][valueChain] ??= {};

      const existing = ret[impactCode][valueChain][valence];
      if (existing) {
        existing.value += value * weight;
      } else {
        ret[impactCode][valueChain][valence] = { ...impactScore, value: value * weight };
      }
    });
  });

  return ret;
}

export function weightImpactScores(list: ImpactScoresByIVV[], weightList: number[]) {
  const merged = mergeImpactScores(list, weightList);
  const sumOfWeights = sum(weightList);
  return mapImpactScoresValues(merged, value => value / sumOfWeights);
}

/**
 * Override impact scores in first parameter (base)
 * with impact scores in the second parameter (override)
 *
 * NOTE: ImpactScoresByIVV may not contain datums for value chains and valences with zero score.
 * Thus, base scores will only be overriden with zeros where explicit zero override datums are present.
 */
export function overrideIndividualImpactScoreDatums(
  base: ImpactScoresByIVV,
  override: ImpactScoresByIVV,
): ImpactScoresByIVV {
  const ret = cloneDeep(base);
  forEachImpactScoreByIVV(override, datum => {
    const { impactCode, valueChain, valence } = datum;
    if (!ret[impactCode]) {
      ret[impactCode] = {};
    }
    if (!ret[impactCode][valueChain]) {
      ret[impactCode][valueChain] = {};
    }
    if (!ret[impactCode][valueChain][valence]) {
      ret[impactCode][valueChain][valence] = { ...datum };
    } else {
      ret[impactCode][valueChain][valence].value = datum.value;
    }
  });
  return ret;
}

/**
 * Override whole impact categories in first parameter (base)
 * with impact scores in the second parameter (override) and returns
 * new scores without mutating the arguments.
 */
export function overrideWholeImpactCategories(
  _base: ImpactScoresByIVV,
  _override: ImpactScoresByIVV,
): ImpactScoresByIVV {
  const base = cloneDeep(_base);
  const override = cloneDeep(_override);
  return { ...base, ...override };
}

/**
 * Helper for performing a .map() -like
 * transformation to an ImpactScoresByIVV object
 */
export function mapImpactScoresDatums(
  impactScoresByIVV: ImpactScoresByIVV,
  iteratee: (value: ImpactScoreDatum) => ImpactScoreDatum | undefined,
): ImpactScoresByIVV {
  const ret: ImpactScoresByIVV = {};
  values(impactScoresByIVV).forEach(impactScoresByVV => {
    values(impactScoresByVV).forEach(impactScoresByV => {
      values(impactScoresByV).forEach(impactScore => {
        const { impactCode, valueChain, valence } = impactScore;
        if (!ret[impactCode]) {
          ret[impactCode] = {};
        }
        if (!ret[impactCode][valueChain]) {
          ret[impactCode][valueChain] = {};
        }
        const value = iteratee(impactScore);
        if (value) {
          ret[impactCode][valueChain][valence] = value;
        }
      });
    });
  });
  return ret;
}

export function omitZeroImpactScores(impactScoresByIVV: ImpactScoresByIVV): ImpactScoresByIVV {
  return mapImpactScoresDatums(impactScoresByIVV, d => (d.value ? d : undefined));
}

/**
 * Helper for performing a .map() -like
 * transformation to the score values
 * of a ImpactScoresByIVV object
 */
export function mapImpactScoresValues(
  impactScoresByIVV: ImpactScoresByIVV,
  iteratee: (value: number) => number | undefined,
): ImpactScoresByIVV {
  return mapImpactScoresDatums(impactScoresByIVV, impactScoreDatum => {
    const value = iteratee(impactScoreDatum.value);
    if (value === undefined) {
      return undefined;
    }
    return {
      ...impactScoreDatum,
      value,
    };
  });
}

/**
 * Helper for performing a .map() -like
 * transformation to an ImpactScoresByPIVV object
 */
export function mapImpactScoresByPIVVDatums(
  impactScoresByPIVV: ImpactScoresByPIVV,
  iteratee: (value: ImpactScoreDatum) => ImpactScoreDatum,
): ImpactScoresByPIVV {
  return mapValues(impactScoresByPIVV, impactScoresByIVV => mapImpactScoresDatums(impactScoresByIVV, iteratee));
}

/**
 * Helper for performing a .map() -like
 * transformation to the score values
 * of a ImpactScoresByIVV object
 */
export function mapImpactScoresByPIVVValues(
  impactScoresByPIVV: ImpactScoresByPIVV,
  iteratee: (value: number) => number,
): ImpactScoresByPIVV {
  return mapImpactScoresByPIVVDatums(impactScoresByPIVV, impactScoreDatum => ({
    ...impactScoreDatum,
    value: iteratee(impactScoreDatum.value),
  }));
}

/**
 * Pack ImpactScoresByIVV to a tinier representation
 */
export function packImpactScoresByIVV(impactScoresByIVV: ImpactScoresByIVV): PackedImpactScoresByIVV {
  const ret: PackedImpactScoresByIVV = {};
  values(impactScoresByIVV).forEach(impactScoresByVV => {
    values(impactScoresByVV).forEach(impactScoresByV => {
      values(impactScoresByV).forEach(impactScore => {
        const { impactCode, valueChain, valence } = impactScore;
        if (!ret[impactCode]) {
          ret[impactCode] = {};
        }
        if (!ret[impactCode][valueChain]) {
          ret[impactCode][valueChain] = {};
        }
        ret[impactCode][valueChain][valence] = impactScore.value || 0;
      });
    });
  });
  return ret;
}

/**
 * Pack ImpactScoresByIVV to a tinier representation
 */
export function unpackImpactScoresByIVV(impactScoresByIVV: PackedImpactScoresByIVV): ImpactScoresByIVV {
  const ret: ImpactScoresByIVV = {};
  Object.keys(impactScoresByIVV).forEach(impactCode => {
    Object.keys(impactScoresByIVV[impactCode]).forEach(valueChain => {
      Object.keys(impactScoresByIVV[impactCode][valueChain]).forEach(valence => {
        if (!ret[impactCode]) {
          ret[impactCode] = {};
        }
        if (!ret[impactCode][valueChain]) {
          ret[impactCode][valueChain] = {};
        }
        ret[impactCode][valueChain][valence] = {
          impactCode: impactCode as ImpactCategoryId,
          valueChain: valueChain as ValueChain,
          valence: valence as Valence,
          value: impactScoresByIVV[impactCode][valueChain][valence] || 0,
        };
      });
    });
  });
  return ret;
}

/**
 * Pick score for impact scores for given
 * impact code, value chain and valence.
 *
 * If value chain is is not given, will pick a sum
 * of scores for all value chains
 *
 * If valence is not given will pick net score
 * for the category (and value chain if given).
 */
export function pickScore(
  impactScoresByIVV: ImpactScoresByIVV,
  impactCode: string,
  valueChain: ValueChain | undefined,
  valence: Valence | undefined,
): number {
  if (!get(impactScoresByIVV, impactCode, undefined)) {
    return 0;
  }

  if (!valence) {
    return (
      pickScore(impactScoresByIVV, impactCode, valueChain, Valence.POSITIVE) -
      pickScore(impactScoresByIVV, impactCode, valueChain, Valence.NEGATIVE)
    );
  }

  if (!valueChain) {
    const valueChains = [ValueChain.ANY, ValueChain.DOWNSTREAM, ValueChain.INTERNAL, ValueChain.UPSTREAM];
    return sumBy(valueChains, vc => pickScore(impactScoresByIVV, impactCode, vc, valence));
  }

  return get(impactScoresByIVV, [impactCode, valueChain, valence, 'value'], 0);
}

const _valences = [Valence.POSITIVE, Valence.NEGATIVE] as const;
export const orderedImpactValences = chain(ImpactCategoryId)
  .values()
  .flatMap((impactCode: ImpactCategoryId) => _valences.map(valence => ({ valence, impactCode })))
  .value();

export function getScoreByImpactValence(data: ImpactScoresByIVV, valueChain?: ValueChain): ByIV<number> {
  return orderedImpactValences.reduce((acc, { valence, impactCode }) => {
    if (!get(acc, impactCode, undefined)) {
      acc[impactCode] = {};
    }
    acc[impactCode][valence] = pickScore(data, impactCode, valueChain, valence);
    return acc;
  }, {} as Dictionary<NumberHash>);
}

export function packToStringWithoutValueChains(impactScores: ImpactScoresByIVV): string {
  const byIv = getScoreByImpactValence(impactScores);
  return chain(byIv)
    .values()
    .flatMap(byV => values(byV).map(val => (val === 0 ? '0' : val.toFixed(3))))
    .value()
    .join(',');
}

export function unpackStringToImpactScores(impactString: string): ImpactScoresByIVV {
  const splitStrings = impactString.split(',');
  if (splitStrings.length !== orderedImpactValences.length) {
    throw Error("Impact string doesn't match expected format (UI vs. backend version mismatch?)");
  }
  const datums = orderedImpactValences.map(({ valence, impactCode }, i) => {
    const value = (() => {
      try {
        return parseFloat(splitStrings[i]);
      } catch (e) {
        if (e instanceof Error) {
          throw Error(`Parsing impact scores from string failed with message: ${e.message}`);
        }
        throw e;
      }
    })();
    const datum: ImpactScoreDatum = {
      impactCode,
      valence,
      value,
      valueChain: ValueChain.ANY,
    };
    return datum;
  });
  return keyImpactScoreData(datums);
}

export function getNetScoreTotal(data: ImpactScoresByIVV) {
  const positiveSum = sumBy(allDimensions, dimension =>
    getDimensionTotal(data, dimension, undefined, Valence.POSITIVE),
  );
  const negativeSum = sumBy(allDimensions, dimension =>
    getDimensionTotal(data, dimension, undefined, Valence.NEGATIVE),
  );

  return positiveSum - negativeSum;
}

export function getDimensionTotal(
  data: ImpactScoresByIVV,
  dimension: Dimension,
  valueChain: ValueChain | undefined,
  valence: Valence,
) {
  const impactCodes = ORDERED_IMPACTS_BY_DIMENSION[dimension];
  return sumBy(impactCodes, code => pickScore(data, code, valueChain, valence));
}

export function getDimensionNetTotal(data: ImpactScoresByIVV | undefined, dimension: Dimension) {
  if (!data) {
    return undefined;
  }
  return (
    getDimensionTotal(data, dimension, undefined, Valence.POSITIVE) -
    getDimensionTotal(data, dimension, undefined, Valence.NEGATIVE)
  );
}

export function getImpactNetTotal(data: ImpactScoresByIVV, impactCode: string) {
  return (
    pickScore(data, impactCode, undefined, Valence.POSITIVE) - pickScore(data, impactCode, undefined, Valence.NEGATIVE)
  );
}

export function toDimensionScore(impactScoresByIVV: ImpactScoresByIVV, dimension: Dimension, valence: Valence) {
  const impactCodes = orderedImpactsByDimension[dimension];
  return sum(impactCodes.map(code => pickScore(impactScoresByIVV, code, undefined, valence)));
}

export function toNetDimensionScore(impactScoresByIVV: ImpactScoresByIVV, dimension: Dimension) {
  const positive = toDimensionScore(impactScoresByIVV, dimension, Valence.POSITIVE);
  const negative = toDimensionScore(impactScoresByIVV, dimension, Valence.NEGATIVE);
  return positive - negative;
}

export function computeDimensionImpactScore(impactScoresByIVV: ImpactScoresByIVV, dimension: Dimension) {
  let ret = 0;
  forEachImpactScoreByIVV(impactScoresByIVV, score => {
    if (!isImpactCategoryVisible(score.impactCode)) {
      return;
    }
    if (score.impactCode.startsWith(dimension)) {
      if (score.valence === Valence.POSITIVE) {
        ret += score.value;
      } else {
        ret -= score.value;
      }
    }
  });
  return ret;
}

export const keyImpactScoreData = (impactScores: ImpactScoreDatum[]) =>
  impactScores.reduce<ImpactScoresByIVV>((result, impactScore) => {
    const { impactCode, valueChain, valence, value } = impactScore;
    if (!get(result, impactCode, undefined)) {
      result[impactCode] = {};
    }
    if (!get(result, [impactCode, valueChain], undefined)) {
      result[impactCode][valueChain] = {};
    }
    result[impactCode][valueChain][valence] = {
      impactCode,
      valence,
      value,
      valueChain,
    };
    return result;
  }, {});

export function emptyImpactByValence<T>(defaultValue?: T) {
  return values(ImpactCategoryId).reduce(
    (acc, key) => {
      acc[key as ImpactCategoryId] = { O: defaultValue, N: defaultValue, P: defaultValue };
      return acc;
    },
    {} as ByImpactValence<T | undefined>,
  );
}

export function flattenByPIV<T>(dataByIV: ByImpactValence<T | undefined>): T[] {
  const results: T[] = [];
  for (const byV of values(dataByIV)) {
    for (const datum of values(byV)) {
      if (!datum) {
        continue;
      }
      results.push(datum);
    }
  }
  return results;
}

export function keyByIV<T extends { impactCode: ImpactCategoryId; valence: Valence }>(data: T[]) {
  return chain(data)
    .groupBy(datum => datum.impactCode)
    .mapValues(d => keyBy(d, datum => datum.valence))
    .value() as ByImpactValence<T>;
}

export function groupByIV<T extends { impactCode: ImpactCategoryId; valence: Valence }, K extends keyof T>(
  data: T[],
  key: K,
): ByImpactValence<{ [key: string]: T }> {
  return chain(data)
    .groupBy(datum => datum.impactCode)
    .mapValues(d =>
      mapValues(
        // I'm running out of letters lol
        groupBy(d, datum => datum.valence),
        da => keyBy(da, datum => datum[key]),
      ),
    )
    .value() as ByImpactValence<{ [key: string]: T }>;
}

export function* forEachImpactScoreByImpactValence<T, U>(
  impactScoresByIVV: ByImpactValence<T>,
  func: (datum: T, impactCode: ImpactCategoryId, valence: Valence) => U,
) {
  for (const impactCode of keys(impactScoresByIVV) as ImpactCategoryId[]) {
    const impactScoresByVV = impactScoresByIVV[impactCode];
    for (const valence of keys(impactScoresByVV) as Valence[]) {
      const impactScoresByV = impactScoresByVV[valence];
      yield func(impactScoresByV, impactCode, valence);
    }
  }
}

export function getValenceSums(scores: ImpactScoreAttributesByIV | undefined): {
  negativeSum: number;
  positiveSum: number;
} {
  let negativeSum = 0;
  let positiveSum = 0;

  for (const key in scores) {
    if (key.endsWith('N')) {
      const negScore = scores[key as ImpactScoreIVKey];
      if (negScore) {
        negativeSum += negScore;
      }
    } else if (key.endsWith('P')) {
      const posScore = scores[key as ImpactScoreIVKey];
      if (posScore) {
        positiveSum += posScore;
      }
    }
  }

  return { negativeSum, positiveSum };
}
